import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
	Button,
	Alert,
	Spinner
} from '../ui';
import { useClientCreate, useAccountCreate } from '../features/clients';
import { ScanService } from '../../services';
import { useUser } from '../../contexts/UserContext';
import { useToast, useConfirm } from '../ui';
import { AddClientForm } from './AddClientForm';
import { AddAccountForm } from './AddAccountForm';
import { OnboardingProgress } from './OnboardingProgress';
import { WizardStep } from './WizardStep';
import { PlatformTimeSelector } from './PlatformTimeSelector';
import { RiskSettingsSelector } from './RiskSettingsSelector';

/**
 * SetupWizard - Main onboarding wizard container
 * 
 * Orchestrates the 4-step onboarding flow:
 * 1. Create Client
 * 2. Add Talent Account
 * 3. Choose Platforms & Time Range
 * 4. Risk Sensitivity Settings
 */
export function SetupWizard({ onComplete, onCancel }) {
	const toast = useToast();
	const confirmAction = useConfirm();
	const [currentStep, setCurrentStep] = useState(1);
	const [wizardData, setWizardData] = useState({
		client: null,
		account: null,
		platforms: getDefaultPlatforms(),
		dateRange: getDefaultDateRange(),
		riskSettings: getDefaultRiskSettings(),
	});
	const [formValidity, setFormValidity] = useState({
		step1: false,
		step2: false,
		step3: true, // Step 3 pre-selects from the chosen account
		step4: true, // Step 4 is always valid since defaults are set
	});
	const [scanId, setScanId] = useState(null);
	const [isScanning, setIsScanning] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Refs for form submission
	const clientFormRef = useRef();
	const accountFormRef = useRef();

	// Hooks for data operations
	const { loading: clientLoading, error: clientError } = useClientCreate();
	const { loading: accountLoading, error: accountError } = useAccountCreate();
	const { completeOnboarding } = useUser();

	// Scan creation state
	const [scanLoading, setScanLoading] = useState(false);
	const [scanError, setScanError] = useState(null);
	const [progressRestored, setProgressRestored] = useState(false);

	// Persist wizard state to localStorage
	useEffect(() => {
		const saved = localStorage.getItem('repcheck_onboarding_state');
		if (saved) {
			try {
				const parsed = JSON.parse(saved);
				// Only restore if the data is valid
				if (parsed && typeof parsed === 'object') {
					// Restore wizard data with validation
					if (parsed.wizardData) {
						const validatedData = validateAndCleanWizardData(parsed.wizardData);
						setWizardData(prev => ({ ...prev, ...validatedData }));
						setProgressRestored(true);
						// Hide the message after 5 seconds
						setTimeout(() => setProgressRestored(false), 5000);
					}
					// Restore current step or infer it from data
					if (parsed.currentStep) {
						setCurrentStep(parsed.currentStep);
					} else {
						// Infer step from available data
						const inferredStep = inferStepFromData(parsed.wizardData || parsed);
						setCurrentStep(inferredStep);
						if (inferredStep > 1) {
							setProgressRestored(true);
							// Hide the message after 5 seconds
							setTimeout(() => setProgressRestored(false), 5000);
						}
					}
				}
			} catch (error) {
				console.warn('Failed to restore onboarding state:', error);
				// Clear invalid data
				localStorage.removeItem('repcheck_onboarding_state');
			}
		}
	}, []);

	// Auto-save to localStorage (save both data and current step)
	useEffect(() => {
		// Only save if wizardData has meaningful content (not just initial state)
		if (wizardData.client || wizardData.account || wizardData.platforms.length > 0) {
			const stateToSave = {
				wizardData,
				currentStep,
				timestamp: Date.now()
			};
			localStorage.setItem('repcheck_onboarding_state', JSON.stringify(stateToSave));
		}
	}, [wizardData, currentStep]);

	// Clear onboarding state when completed
	const clearOnboardingState = useCallback(() => {
		localStorage.removeItem('repcheck_onboarding_state');
	}, []);

	const handleCancel = async () => {
		// The old guard dialog was dead code - nothing ever opened it - so Esc
		// or "Exit" left setup with no confirmation at all.
		const ok = await confirmAction({
			title: 'Exit setup?',
			description: 'Your progress is saved and you can finish later from the dashboard.',
			confirmLabel: 'Exit setup',
		});
		if (!ok) return;
		try {
			await completeOnboarding();
		} catch (error) {
			console.error('Error marking onboarding as completed:', error);
		}
		clearOnboardingState();
		onCancel();
	};

	// Step validation
	const isStepValid = (step) => {
		switch (step) {
			case 1:
				return formValidity.step1;
			case 2:
				return formValidity.step2;
			case 3:
				return formValidity.step3;
			case 4:
				return formValidity.step4;
			default:
				return false;
		}
	};

	// Get submitting message based on current step
	const getSubmittingMessage = () => {
		switch (currentStep) {
			case 1: return 'Creating Client...';
			case 2: return 'Creating Account...';
			default: return 'Processing...';
		}
	};

	// Navigation handlers
	const handleNext = async () => {
		if (isSubmitting) return; // Prevent multiple submissions

		if (currentStep < 4 && isStepValid(currentStep)) {
			// If on step 1 or 2, submit the form first
			if (currentStep === 1 && clientFormRef.current) {
				setIsSubmitting(true);
				try {
					await clientFormRef.current.submit();
					// handleClientCreated will be called automatically from form success
				} catch (error) {
					console.error('Error submitting client form:', error);
					setIsSubmitting(false);
				}
				return;
			} else if (currentStep === 2 && accountFormRef.current) {
				setIsSubmitting(true);
				try {
					await accountFormRef.current.submit();
					// handleAccountCreated will be called automatically from form success
				} catch (error) {
					console.error('Error submitting account form:', error);
					setIsSubmitting(false);
				}
				return;
			} else {
				// For steps 3 and 4, just advance
				setCurrentStep(currentStep + 1);
			}
		}
	};

	const handlePrevious = () => {
		if (currentStep > 1) {
			setCurrentStep(currentStep - 1);
		}
	};

	// Get current loading state
	const getCurrentLoading = () => {
		switch (currentStep) {
			case 1: return clientLoading;
			case 2: return accountLoading;
			case 4: return scanLoading;
			default: return false;
		}
	};

	// Keyboard navigation
	useEffect(() => {
		const handleKeyDown = (event) => {
			// Don't interfere if user is typing in an input
			if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA' || event.target.tagName === 'SELECT') {
				return;
			}

			switch (event.key) {
				case 'ArrowRight':
				case 'Enter':
					if (isStepValid(currentStep) && !getCurrentLoading() && !isSubmitting) {
						if (currentStep < 4) {
							handleNext();
						} else {
							handleCreateScan();
						}
					}
					break;
				case 'ArrowLeft':
					if (currentStep > 1 && !getCurrentLoading()) {
						handlePrevious();
					}
					break;
				case 'Escape':
					if (!getCurrentLoading()) {
						handleCancel();
					}
					break;
				default:
					break;
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
		// The handlers are plain functions closing over the same state listed
		// here, so they are never stale in practice; listing them would re-bind
		// the listener every render and one is defined below this effect.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentStep, isStepValid, getCurrentLoading, isSubmitting]);

	// Step completion handlers
	const handleClientCreated = (client) => {
		setWizardData(prev => ({ ...prev, client }));
		setIsSubmitting(false);
		// Advance to next step
		setCurrentStep(2);
	};

	const handleAccountCreated = (account) => {
		setWizardData(prev => ({ ...prev, account }));
		setIsSubmitting(false);
		// Advance to next step
		setCurrentStep(3);
	};

	const handlePlatformsSelected = (platforms) => {
		setWizardData(prev => ({ ...prev, platforms }));
	};

	const handleDateRangeSelected = (dateRange) => {
		setWizardData(prev => ({ ...prev, dateRange }));
	};

	const handleRiskSettingsSelected = (riskSettings) => {
		setWizardData(prev => ({ ...prev, riskSettings }));
	};

	// Final scan creation
	const handleCreateScan = async () => {
		if (!isStepValid(4)) return;

		try {
			setScanLoading(true);
			setScanError(null);

			// Validate wizard data before creating scan
			const scanRequest = buildScanRequest(wizardData);

			const result = await ScanService.createScan(scanRequest);

			if (result && result.scanId) {
				setScanId(result.scanId);
				setIsScanning(true);

				// Mark onboarding as completed
				try {
					await completeOnboarding();
				} catch (error) {
					console.error('Error marking onboarding as completed:', error);
					// Don't fail the entire process for this
				}

				// Only clear onboarding state after everything is successful
				try {
					if (onComplete) {
						await onComplete(result);
					}
					// Clear state only after successful completion
					clearOnboardingState();
				} catch (error) {
					console.error('Error in onComplete callback:', error);
					// Don't clear state if there's an error - user can resume
				}
			} else {
				throw new Error('Failed to create scan - invalid response');
			}
		} catch (error) {
			console.error('Failed to create scan:', error);
			setScanError(error.message);

			// Provide more helpful error messages
			let userMessage = error.message;
			if (error.message.includes('Missing required data')) {
				userMessage = 'Some setup data is incomplete. Please review and complete all steps before creating the scan.';
			} else if (error.message.includes('Client data is missing')) {
				userMessage = 'Client information is missing. Please complete Step 1 of the setup.';
			} else if (error.message.includes('Account data is missing')) {
				userMessage = 'Account information is missing. Please complete Step 2 of the setup.';
			}

			toast.error('Could not create the scan', { description: userMessage });
		} finally {
			setScanLoading(false);
		}
	};

	// Build scan request from wizard data
	const buildScanRequest = (data) => {
		// Validate required data with better error messages
		const missingFields = [];

		if (!data.client || !data.client._id) {
			missingFields.push('client');
		}
		if (!data.account || !data.account._id) {
			missingFields.push('account');
		}
		if (!data.platforms || data.platforms.length === 0) {
			missingFields.push('platforms');
		}
		if (!data.dateRange || !data.dateRange.startDate || !data.dateRange.endDate) {
			missingFields.push('date range');
		}
		if (!data.riskSettings) {
			missingFields.push('risk settings');
		}

		if (missingFields.length > 0) {
			throw new Error(`Missing required data: ${missingFields.join(', ')}. Please complete the setup steps.`);
		}

		// Convert wizard data to scan service format
		return {
			clientId: data.client._id,
			accountId: data.account._id,
			platform: data.platforms[0], // Use first platform for now
			username: data.account.handle, // Required field for validation
			startDate: data.dateRange.startDate,
			endDate: data.dateRange.endDate,
			sensitivityLevel: data.riskSettings.sensitivityLevel || 'medium',
			enabledCategories: data.riskSettings.enabledCategories || [
				"hate_speech",
				"harassment",
				"explicit",
				"violence",
				"political",
				"cultural",
				"misconduct",
				"drug_use",
				"self_harm",
				"spam",
				"other",
			],
			customKeywords: data.riskSettings.customKeywords || [],
			isOnboarding: true, // Flag for backend
		};
	};

	// Get current error
	const getCurrentError = () => {
		switch (currentStep) {
			case 1: return clientError;
			case 2: return accountError;
			case 4: return scanError;
			default: return null;
		}
	};

	// If scan is in progress, show progress component
	if (isScanning && scanId) {
		return (
			<div className="min-h-screen bg-muted flex items-center justify-center p-4">
				<div className="bg-card rounded-lg shadow-lg max-w-4xl w-full p-8">
					<div className="text-center">
						<div className="text-2xl font-semibold mb-2">Setting Up Your First Scan</div>
						<p className="text-muted-foreground mb-8">
							We're configuring RepCheck to monitor your talent's social media presence.
						</p>
						<Spinner className="mx-auto mb-4" size="lg" />
						<h3 className="text-lg font-semibold mb-2">Initializing Scan</h3>
						<p className="text-muted-foreground">
							This may take a few minutes. We'll notify you when it's complete.
						</p>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-muted flex items-center justify-center p-4">
			<div className="bg-card rounded-lg shadow-lg w-full max-w-5xl mx-auto">
				{/* Header */}
				<div className="p-8 border-b border-border">
					<OnboardingProgress currentStep={currentStep} totalSteps={4} />
					{progressRestored && (
						<div className="mt-4 p-3 bg-brand-50 border border-brand-200 rounded-lg dark:bg-brand-500/10 dark:border-brand-500/30">
							<div className="flex items-center">
								<svg className="h-5 w-5 text-brand-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
									<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
								</svg>
								<span className="text-brand-800 text-sm font-medium">
									Progress restored - continuing from Step {currentStep}
								</span>
							</div>
						</div>
					)}
					<h1 className="text-3xl font-bold text-foreground mt-6">RepCheck Setup</h1>
					<p className="text-lg text-muted-foreground mt-3">
						{progressRestored
							? "Continuing your RepCheck setup where you left off."
							: "Let's get you started with RepCheck in just a few steps."
						}
					</p>
				</div>

				{/* Content with improved spacing */}
				<div className="p-8 min-h-[500px]">
					{getCurrentError() && (
						<Alert variant="error" className="mb-6">
							{getCurrentError()}
						</Alert>
					)}

					{/* Step 1: Create Client */}
					{currentStep === 1 && (
						<WizardStep
							step={1}
							title="Create Your First Client"
							description="Start by creating a client to monitor"
						>
							<AddClientForm
								ref={clientFormRef}
								onSuccess={handleClientCreated}
								isOnboarding={true}
								onValidityChange={(isValid) => {
									setFormValidity(prev => {
										if (prev.step1 !== isValid) {
											return { ...prev, step1: isValid };
										}
										return prev;
									});
								}}
							/>
						</WizardStep>
					)}

					{/* Step 2: Add Talent Account */}
					{currentStep === 2 && (
						<WizardStep
							step={2}
							title="Add Talent to Monitor"
							description="Enter the social media account you want to monitor"
						>
							<AddAccountForm
								ref={accountFormRef}
								clientId={wizardData.client?._id}
								clientName={wizardData.client?.name}
								onSuccess={handleAccountCreated}
								isOnboarding={true}
								onValidityChange={(isValid) => {
									setFormValidity(prev => {
										if (prev.step2 !== isValid) {
											return { ...prev, step2: isValid };
										}
										return prev;
									});
								}}
							/>
						</WizardStep>
					)}

					{/* Step 3: Choose Platforms & Time Range */}
					{currentStep === 3 && (
						<WizardStep
							step={3}
							title="Select Platforms & Timeline"
							description="Choose which platforms to monitor and the time period"
						>
							<PlatformTimeSelector
								selectedAccount={wizardData.account}
								platforms={wizardData.platforms}
								dateRange={wizardData.dateRange}
								onPlatformsChange={handlePlatformsSelected}
								onDateRangeChange={handleDateRangeSelected}
							/>
						</WizardStep>
					)}

					{/* Step 4: Risk Sensitivity Settings */}
					{currentStep === 4 && (
						<WizardStep
							step={4}
							title="Set Content Monitoring Level"
							description="Configure how sensitive RepCheck should be to potential issues"
						>
							<RiskSettingsSelector
								value={wizardData.riskSettings}
								onChange={handleRiskSettingsSelected}
								simplified={true}
							/>
						</WizardStep>
					)}
				</div>

				{/* Footer */}
				<div className="px-8 py-6 border-t border-border bg-muted rounded-b-lg">
					<div className="flex justify-between items-center">
						<Button
							variant="ghost"
							onClick={handleCancel}
							disabled={getCurrentLoading()}
							className="text-muted-foreground hover:text-foreground"
							aria-label="Exit setup process"
						>
							Skip Setup
						</Button>

						<div className="flex items-center gap-4">
							{currentStep > 1 && (
								<Button
									variant="outline"
									onClick={handlePrevious}
									disabled={getCurrentLoading()}
									className="px-6 py-2"
									aria-label="Go to previous step"
								>
									← Previous
								</Button>
							)}

							{currentStep < 4 ? (
								<Button
									onClick={handleNext}
									disabled={!isStepValid(currentStep) || getCurrentLoading() || isSubmitting}
									loading={getCurrentLoading() || isSubmitting}
									className="bg-brand-600 hover:bg-brand-700 text-white font-semibold px-8 py-2 shadow-lg"
									aria-label="Continue to next step"
								>
									{isSubmitting ? getSubmittingMessage() : 'Continue →'}
								</Button>
							) : (
								<Button
									onClick={handleCreateScan}
									disabled={!isStepValid(currentStep) || getCurrentLoading()}
									loading={getCurrentLoading()}
									className="bg-brand-600 hover:bg-brand-700 text-white font-semibold px-8 py-2 shadow-lg"
									aria-label="Start monitoring setup"
								>
									{getCurrentLoading() ? 'Starting Scan...' : 'Start Monitoring →'}
								</Button>
							)}
						</div>
					</div>

					{/* Progress indicator and keyboard hints */}
					<div className="mt-4 text-center space-y-2">
						<p className="text-sm text-muted-foreground">
							Step {currentStep} of 4 - {Math.round((currentStep / 4) * 100)}% Complete
						</p>
						<div className="text-xs text-muted-foreground flex items-center justify-center space-x-4">
							<span>Use ← → arrow keys to navigate</span>
							<span>•</span>
							<span>Press Esc to exit</span>
						</div>
					</div>
				</div>
			</div>

		</div>
	);
}

// Helper functions
function getDefaultDateRange() {
	const endDate = new Date();
	const startDate = new Date();
	startDate.setDate(startDate.getDate() - 7); // 7 days ago

	return {
		startDate: startDate.toISOString().split('T')[0],
		endDate: endDate.toISOString().split('T')[0],
	};
}

function getDefaultPlatforms() {
	// Empty by design - PlatformTimeSelector derives the default from the
	// account the user actually added. Hardcoding 'X' here forced every
	// onboarding down the X path regardless of what they were monitoring.
	return [];
}

function getDefaultRiskSettings() {
	return {
		hateSpeech: 'medium',
		harassment: 'medium',
		explicitContent: 'medium',
		keywords: [],
	};
}

function validateAndCleanWizardData(data) {
	// Deep clone to avoid mutating original
	const cleaned = { ...data };

	// Validate and clean client data
	if (cleaned.client) {
		if (!cleaned.client._id || typeof cleaned.client._id !== 'string') {
			console.warn('Invalid client data found, clearing...');
			cleaned.client = null;
		}
	}

	// Validate and clean account data
	if (cleaned.account) {
		if (!cleaned.account._id || typeof cleaned.account._id !== 'string' ||
			!cleaned.account.handle || typeof cleaned.account.handle !== 'string') {
			console.warn('Invalid account data found, clearing...');
			cleaned.account = null;
		}
	}

	// Validate and clean platforms
	if (cleaned.platforms) {
		if (!Array.isArray(cleaned.platforms) || cleaned.platforms.length === 0) {
			console.warn('Invalid platforms data found, resetting to defaults...');
			cleaned.platforms = getDefaultPlatforms();
		}
	}

	// Validate and clean date range
	if (cleaned.dateRange) {
		if (!cleaned.dateRange.startDate || !cleaned.dateRange.endDate) {
			console.warn('Invalid date range found, resetting to defaults...');
			cleaned.dateRange = getDefaultDateRange();
		}
	}

	// Validate and clean risk settings
	if (cleaned.riskSettings) {
		if (typeof cleaned.riskSettings !== 'object') {
			console.warn('Invalid risk settings found, resetting to defaults...');
			cleaned.riskSettings = getDefaultRiskSettings();
		}
	}

	return cleaned;
}

function inferStepFromData(data) {
	if (!data) return 1;

	// If no client, start at step 1
	if (!data.client || !data.client._id) {
		return 1;
	}

	// If client but no account, go to step 2
	if (!data.account || !data.account._id) {
		return 2;
	}

	// If client and account but platforms/dateRange not configured, go to step 3
	if (!data.platforms || data.platforms.length === 0 || !data.dateRange) {
		return 3;
	}

	// Everything is configured, go to step 4
	return 4;
} 